<template>
  <div class="app-bg">
    <!-- Login Dialog -->
    <v-dialog v-model="loginDialog" width="400">
      <LoginDialog />
    </v-dialog>

    <!-- ✅ If user is NOT verified -->
    <v-container
      v-if="currentUser && !currentUser.isVerified"
      fill-height
      fluid
      class="verify-screen text-center d-flex flex-column align-center justify-center"
    >
      <v-card class="pa-6 text-center rounded-xl elevation-8 verify-card">
        <v-img
          src="@/assets/nikah-connect-logo.png"
          contain
          max-width="120"
          class="mx-auto mb-4"
        />
        <h2 class="font-weight-bold mb-3" style="color: #2a6465">
          Verify Your Email
        </h2>
        <p class="grey--text text--darken-1 mb-4">
          As-salamu Alaikum
          <b class="text-capitalize font-weight-bold">{{ currentUser.name }}</b
          >,<br />
          Before you can explore Nikah Connect and view profiles, please verify
          your email address.
        </p>

        <v-divider class="my-4"></v-divider>

        <p class="mb-5" style="font-style: italic; color: #555">
          “When a man marries, he has fulfilled half of his religion, so let him
          fear Allah regarding the remaining half.” <br />
          <span style="font-size: 13px">(Tirmidhi 1086)</span>
        </p>

        <v-btn
          color="primary"
          class="mb-3"
          :loading="loading.resend"
          @click="resendVerificationEmail(currentUser.email)"
          block
          large
        >
          Resend Verification Email
        </v-btn>

        <v-btn
          outlined
          color="primary"
          :loading="loading.check"
          @click="refreshVerificationStatus"
          block
          large
        >
          Check Verification Status
        </v-btn>

        <p class="mt-5 grey--text text--darken-1" style="font-size: 13px">
          JazakAllah Khair for helping us keep our community authentic.
        </p>
      </v-card>
    </v-container>

    <!-- ✅ If user IS verified -->
    <v-main v-else-if="globalStatus === false">
      <router-view></router-view>
      <BottomNav />
    </v-main>
  </div>
</template>

<script>
import RegisterStoreModule from "@/mixins/RegisterStoreModule";
import layoutStore from "./store";
import SignUp from "@/modules/Authentication/components/SignUp.vue";
import LoginDialog from "../Authentication/components/LoginDialog";
import BottomNav from "@/modules/Layout/components/BottomNavigation.vue";
import authService from "@/modules/Authentication/service.js";
import socket from "@/socket";
import chatStore from "@/modules/Chat/store.js";
import { mapGetters, mapActions, mapMutations } from "vuex";
const pjson = require("../../../package.json");

export default {
  name: "layout",
  mixins: [RegisterStoreModule],

  data() {
    return {
      userId: null,
      authenticated: true,
      polling: null,
      joinedOnce: false,
      loading: { resend: false, check: false },
    };
  },

  async created() {
    try {
      this.registerStoreModule("layout", layoutStore);
      this.registerStoreModule("chat", chatStore);
      await this.initUser();
      await this.loadPreferences();
      this.setGlobalStatus(false);

      socket.on("connect", () => {
        console.log("✅ Socket connected:", socket.id);

        // Wait until user data is ready
        if (this.currentUser?.id) {
          this.joinPrivateRoom();
        } else {
          // Retry once user info is loaded
          const checkUserInterval = setInterval(() => {
            if (this.currentUser?.id) {
              this.joinPrivateRoom();
              clearInterval(checkUserInterval);
            }
          }, 500);
        }
      });

      // 👇 Also handle socket reconnection case
      socket.on("reconnect", () => {
        console.log("🔄 Socket reconnected:", socket.id);
        if (this.currentUser?.id) this.joinPrivateRoom();
      });

      socket.on("onlineUsers", (list) => {
        // console.log("🟢 Online users list received:", list);
        this.setOnlineUsers(list);
      });

      socket.on("disconnect", () => {
        console.log("🔴 Socket disconnected");
      });
    } catch (error) {
      console.log("Error initializing layout:", error);
    }
  },

  methods: {
    ...mapActions("global", [
      "setGlobalStatus",
      "setLoginDialog",
      "initUser",
      "loadPreferences",
    ]),
    ...mapMutations("global", ["setOnlineUsers", "setCurrentUser"]),

    async resendVerificationEmail(email) {
      try {
        this.loading.resend = true;
        await authService.resendVerification({ email: email });

        this.$swal({
          icon: "success",
          title: "Verification Email Sent!",
          html: `
    <p>A verification email has been sent to <b>${email}</b>.</p>
    <p>Please check your inbox — and if you don’t see it within a few minutes,
    be sure to check your <b>Spam</b> or <b>Junk</b> folder.</p>
  `,
          confirmButtonColor: "#2A6465",
        });
      } catch (error) {
        console.error("Resend verification error:", error);
        this.$swal({
          icon: "error",
          title: "Unable to Resend",
          text: "Something went wrong while sending the email. Please try again later.",
          confirmButtonColor: "#E53935",
        });
      } finally {
        this.loading.resend = false;
      }
    },

    async refreshVerificationStatus() {
      try {
        this.loading.check = true;
        await this.initUser();

        if (this.currentUser.isVerified) {
          this.$swal({
            icon: "success",
            title: "Email Verified!",
            text: "Alhamdulillah! Your email has been successfully verified. You can now explore Nikah Connect.",
            confirmButtonColor: "#2A6465",
          });
        } else {
          this.$swal({
            icon: "info",
            title: "Still Not Verified",
            text: "Please check your inbox and click the verification link, then try again.",
            confirmButtonColor: "#2A6465",
          });
        }
      } catch (error) {
        console.log("Check status error:", error);
        this.$swal({
          icon: "error",
          title: "Unable to Check Status",
          text: "There was an issue checking your verification status. Please try again later.",
          confirmButtonColor: "#E53935",
        });
      } finally {
        this.loading.check = false;
      }
    },

    joinPrivateRoom() {
      const userId = this.currentUser?.id;
      if (!userId) return;

      console.log(`📡 Joining socket for user ${userId}`);

      socket.emit("userOnline", { userId: this.currentUser.id });
      socket.emit("join", { userId: this.currentUser.id });
    },
  },

  computed: {
    ...mapGetters("global", ["currentUser", "globalStatus", "loginDialog"]),
    version() {
      return pjson.version;
    },
  },

  components: {
    SignUp,
    LoginDialog,
    BottomNav,
  },
};
</script>

<style scoped>
.app-bg {
  background: linear-gradient(145deg, #e8f5e9, #a5d6a7);
  min-height: 100vh;
  width: 100%;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
}

.verify-screen {
  min-height: 100vh;
  background: linear-gradient(145deg, #f9fafb, #eef6f2);
}

.verify-card {
  max-width: 400px;
  text-align: center;
}
</style>